import axios from "axios";
import { store } from "../store/store";
import api from "./api";
import { CacheTTL, cacheKey, cached } from "./requestCache";
import {
  PaymentMethodDto,
  PaymentMethodInterface,
  PaymentMethodEnum,
  ResponseInterface,
  PaymentInterface,
} from "../interfaces";
import { PaymentExtendedDto, MobilePaymentCoordinatesDto } from "../interfaces/Dtos";

const baseURLAPI = process.env.REACT_APP_API_HOST;

// get all payment methods for a business unit
/**
 * T2. Both public functions below hit the SAME endpoint and differ only in how
 * they filter and map the result client-side, so the request is shared.
 *
 * Returns null rather than [] on failure. The two are indistinguishable at the
 * call site otherwise, and retaining a failed [] would leave the cashier with
 * an empty payment screen for the whole TTL. isSuccessfulResponse cannot be
 * used here: it expects the ResponseInterface envelope and this resolves a bare
 * array, so it would reject every response and retain nothing.
 */
const fetchBUPaymentMethodsRaw = async (
  buCode: string,
  userLogin: string
): Promise<PaymentMethodDto[] | null> => {
  const uri = `${baseURLAPI}/v1/Payment/SearchByBU?BUCode=${buCode}&UserLogin=${userLogin}`;
  try {
    const response = await api.get(uri);
    if (response.status === 200) {
      return response.data.model as PaymentMethodDto[];
    }
  } catch (error) {}
  return null;
};

/**
 * cached, not inflight: the two consumers (Login.saveSession and
 * Layout.handleBusinessUnitChange) call the pair with a full await in between,
 * so the first promise has already settled and left the pending map by the time
 * the second starts. There is no concurrency to collapse here, only sequential
 * repetition, and only retention removes it.
 */
const getBUPaymentMethodsRaw = (buCode: string, userLogin: string) =>
  cached(
    cacheKey("BUPaymentMethods", buCode, userLogin),
    () => fetchBUPaymentMethodsRaw(buCode, userLogin),
    CacheTTL.LOCKS,
    (value) => value !== null
  );

export const getPaymentMethods = async (
  buCode: string,
  userLogin: string
): Promise<PaymentMethodInterface[]> => {
  const paymentMethodsDto = await getBUPaymentMethodsRaw(buCode, userLogin);
  if (paymentMethodsDto !== null) {
    const paymentMethods: PaymentMethodInterface[] = [];
    let bankID: number | undefined = undefined;
    let bankAccountID: number | undefined = undefined;
    paymentMethodsDto.forEach((method) => {
      if (
        method.paymentMethodID !==
        PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL &&
        method.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_ISRL &&
        method.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_IVA
      ) {
        if (method.banks && method.banks.length > 0) {
          if (method.banks.length === 1) {
            bankID = method.banks[0].bankID;
            bankAccountID = method.banks[0].bankAccountID;
          } else if (
            // Los IDs nombrados y la cuenta 25 vienen de la otra rama en dev;
            // el `> 6 && < 9` con cuenta 0 era la forma anterior de decir lo
            // mismo. Se conserva la nueva sobre la lectura cacheada.
            method.paymentMethodID ===
              PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO ||
            method.paymentMethodID ===
              PaymentMethodEnum.PAGO_MERCHANT_TARJETAS_DE_CREDITO
          ) {
            bankID = 27;
            bankAccountID = 25;
          }
        }
        const paymentMethod: PaymentMethodInterface = {
          paymentMethodID: method.paymentMethodID,
          paymentMethodName: method.paymentMethodName,
          currencyID: method.currencyID,
          bankID: bankID,
          bankAccountID: bankAccountID,
          bankAccounts: method.banks,
        };
        bankID = undefined;
        bankAccountID = undefined;
        paymentMethods.push(paymentMethod);
      }
    });

    const sortedPaymentMethods = [...paymentMethods].sort((a, b) =>
      a.paymentMethodName.localeCompare(b.paymentMethodName)
    );

    return sortedPaymentMethods;
  }
  return [];
};

// get all retention methods form a business unit
export const getRetentionMethods = async (
  buCode: string,
  userLogin: string
): Promise<PaymentMethodInterface[]> => {
  const paymentMethodsDto = await getBUPaymentMethodsRaw(buCode, userLogin);
  if (paymentMethodsDto !== null) {
    const retentionMethods: PaymentMethodInterface[] = [];
    paymentMethodsDto.forEach((method) => {
      if (
        method.paymentMethodID ===
        PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL ||
        method.paymentMethodID === PaymentMethodEnum.RETENCION_DE_ISRL ||
        method.paymentMethodID === PaymentMethodEnum.RETENCION_DE_IVA
      ) {
        const retentionMethod: PaymentMethodInterface = {
          paymentMethodID: method.paymentMethodID,
          paymentMethodName: method.paymentMethodName,
          currencyID: method.currencyID,
        };
        retentionMethods.push(retentionMethod);
      }
    });

    return retentionMethods;
  }
  return [];
};

export const getStorePendingPayments = async (
  buCode: string
): Promise<ResponseInterface<PaymentInterface[]>> => {
  const currencyList = store.getState().inmutable.currencyList;
  const uri = `${baseURLAPI}/v1/Payment/Pending?BUCode=${buCode}`;

  try {
    const response = await axios.get(uri);

    if (response.status === 200) {
      return {
        ...response.data.model,
        model: response.data.model.map(
          (payment: PaymentExtendedDto) =>
          ({
            paymentID: payment.paymentID,
            documentID: payment.documentID,
            documentNumber: payment.documentNumber,
            paymentDetailID: payment.paymentDetailID,
            proofOfPayment: payment.attachmentID,
            amount: payment.paymentAmountBaseCurr,
            sourceBankID: payment.sourceBankID,
            destBankID: payment.bankAccountID,
            paymentMethod: {
              paymentMethodID: payment.paymentMethodID,
              paymentMethodName: payment.paymentMethodName,
              currencyID: payment.currencyID,
              bankID: payment.bankAccountID,
              taxes: [],
            },
            igtfAmount: 0,
            igtfPercentage: 0,
            reference: payment.referenceNum,
            currency: {
              id: payment.currencyID,
              name:
                currencyList.find(
                  (pay) => pay.currencyID === payment.currencyID
                )?.currencyName ?? "",
              code:
                currencyList.find(
                  (pay) => pay.currencyID === payment.currencyID
                )?.currencyIsoCode ?? "",
            },
            status: payment.statusID,
            creationDate: payment.paymentDate,
            paymentDate: payment.paymentDetailDate,
            creationUser: payment.creationUser,
            isRetention: [
              PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL,
              PaymentMethodEnum.RETENCION_DE_ISRL,
              PaymentMethodEnum.RETENCION_DE_IVA,
            ].includes(payment.paymentMethodID), // check if inside a list of retentions
          } as PaymentInterface)
        ),
      };
    }
  } catch (error) {
    return (error as any).response.data;
  }

  return {
    message: "",
    model: null,
    didError: true,
    errorMessage: "Hubo un error al obtener los pagos pendientes.",
  };
};

export const getMobilePaymentCoordinates = async (): Promise<MobilePaymentCoordinatesDto[]> => {
  const uri = `${baseURLAPI}/v1/Payment/GetPaymentMobile`;
  try {
    const response = await api.get(uri);

    if (response.status === 200) {
      return response.data.model;
    }
  } catch (error) {
    return (error as any).response.data;
  }

  return [];
};